import React, { useState, useEffect, useCallback } from 'react';
import { Accessibility, Type, Sun, Volume2, VolumeX, X, Eye, RotateCcw } from 'lucide-react';

/**
 * Floating Accessibility Toolbar (bottom-right).
 *
 * Provides:
 *  - Text size: Normal / Large / Larger
 *  - High-contrast yellow-on-black palette
 *  - Read-aloud (Web Speech API — browser-native, no backend cost)
 *  - Reset
 *
 * Settings persist to localStorage. Toggles apply CSS classes to <html>.
 * Read-aloud reads the currently visible `main` (or document body) text.
 *
 * NO external dependencies. NO backend round-trip. Pure browser APIs.
 */

const LS_KEY = 'pai_a11y_v1';

function loadSettings() {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) return { textSize: 'normal', highContrast: false };
    const obj = JSON.parse(raw);
    return {
      textSize: obj.textSize || 'normal',
      highContrast: !!obj.highContrast,
    };
  } catch {
    return { textSize: 'normal', highContrast: false };
  }
}

function saveSettings(s) {
  try { localStorage.setItem(LS_KEY, JSON.stringify(s)); } catch (e) { /* ignore */ }
}

function applyToDOM({ textSize, highContrast }) {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  root.classList.remove('a11y-large-text', 'a11y-larger-text');
  if (textSize === 'large') root.classList.add('a11y-large-text');
  if (textSize === 'larger') root.classList.add('a11y-larger-text');
  root.classList.toggle('a11y-high-contrast', !!highContrast);
}

export default function AccessibilityToolbar() {
  const [open, setOpen] = useState(false);
  const [{ textSize, highContrast }, setState] = useState(loadSettings);
  const [reading, setReading] = useState(false);

  // Apply on mount + whenever state changes
  useEffect(() => {
    applyToDOM({ textSize, highContrast });
    saveSettings({ textSize, highContrast });
  }, [textSize, highContrast]);

  // Stop speech when toolbar unmounts or read toggled off
  useEffect(() => {
    return () => {
      if (typeof window !== 'undefined' && window.speechSynthesis) {
        window.speechSynthesis.cancel();
      }
    };
  }, []);

  const setTextSize = (size) => setState(s => ({ ...s, textSize: size }));
  const toggleContrast = () => setState(s => ({ ...s, highContrast: !s.highContrast }));
  const resetAll = () => setState({ textSize: 'normal', highContrast: false });

  const readAloud = useCallback(() => {
    if (typeof window === 'undefined' || !window.speechSynthesis) {
      alert('Read-aloud is not supported by your browser. Try Chrome, Edge, or Safari.');
      return;
    }
    const synth = window.speechSynthesis;
    if (reading) {
      synth.cancel();
      setReading(false);
      return;
    }
    // Prefer the main content; fall back to whole body
    const main = document.querySelector('main') || document.body;
    const text = (main.innerText || '').replace(/\s+/g, ' ').trim().slice(0, 8000);
    if (!text) return;
    const utter = new SpeechSynthesisUtterance(text);
    utter.rate = 0.95;
    utter.pitch = 1;
    utter.lang = document.documentElement.lang || 'en-US';
    utter.onend = () => setReading(false);
    utter.onerror = () => setReading(false);
    synth.cancel();
    synth.speak(utter);
    setReading(true);
  }, [reading]);

  return (
    <div className="a11y-toolbar-anchor fixed bottom-4 right-4 z-[100]" data-testid="accessibility-toolbar">
      {open && (
        <div
          role="dialog"
          aria-label="Accessibility settings"
          className="mb-3 w-72 rounded-2xl bg-white border-2 border-violet-300 shadow-2xl p-4"
          style={{ background: '#ffffff', color: '#0f172a' }}
        >
          <div className="flex items-center justify-between mb-3">
            <p className="font-extrabold text-slate-900 flex items-center gap-2 text-sm">
              <Accessibility className="w-4 h-4 text-violet-700" /> Accessibility
            </p>
            <button
              onClick={() => setOpen(false)}
              aria-label="Close accessibility settings"
              className="text-slate-500 hover:text-slate-900"
              data-testid="a11y-close"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* Text size group */}
          <div className="mb-3">
            <p className="text-xs font-bold text-slate-700 mb-1.5 flex items-center gap-1">
              <Type className="w-3.5 h-3.5" /> Text size
            </p>
            <div className="flex gap-1.5">
              <SizeBtn label="Normal" pressed={textSize === 'normal'} onClick={() => setTextSize('normal')} testid="a11y-size-normal" />
              <SizeBtn label="Large" pressed={textSize === 'large'} onClick={() => setTextSize('large')} testid="a11y-size-large" />
              <SizeBtn label="Larger" pressed={textSize === 'larger'} onClick={() => setTextSize('larger')} testid="a11y-size-larger" />
            </div>
          </div>

          {/* Contrast toggle */}
          <div className="mb-3">
            <p className="text-xs font-bold text-slate-700 mb-1.5 flex items-center gap-1">
              <Sun className="w-3.5 h-3.5" /> Display
            </p>
            <button
              type="button"
              aria-pressed={highContrast}
              onClick={toggleContrast}
              className={`w-full text-left rounded-lg border-2 px-3 py-2 text-xs font-bold transition ${highContrast ? 'bg-black text-yellow-300 border-yellow-300' : 'bg-white text-slate-900 border-slate-300 hover:border-violet-400'}`}
              data-testid="a11y-contrast-toggle"
            >
              {highContrast ? '✓ High contrast (yellow on black)' : 'High contrast mode'}
            </button>
          </div>

          {/* Read aloud */}
          <div className="mb-3">
            <p className="text-xs font-bold text-slate-700 mb-1.5 flex items-center gap-1">
              <Volume2 className="w-3.5 h-3.5" /> Read aloud
            </p>
            <button
              type="button"
              onClick={readAloud}
              aria-pressed={reading}
              className={`w-full rounded-lg border-2 px-3 py-2 text-xs font-bold flex items-center justify-center gap-2 ${reading ? 'bg-violet-700 text-white border-violet-700' : 'bg-white text-violet-800 border-violet-300 hover:bg-violet-50'}`}
              data-testid="a11y-read-aloud"
            >
              {reading ? <><VolumeX className="w-3.5 h-3.5" /> Stop reading</> : <><Volume2 className="w-3.5 h-3.5" /> Read this page aloud</>}
            </button>
            <p className="text-[10px] text-slate-500 mt-1.5 leading-snug">
              Uses your browser's text-to-speech. No data leaves your device.
            </p>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between pt-2 border-t border-slate-200">
            <button
              type="button"
              onClick={resetAll}
              className="text-[10px] font-bold text-slate-500 hover:text-slate-900 flex items-center gap-1"
              data-testid="a11y-reset"
            >
              <RotateCcw className="w-3 h-3" /> Reset
            </button>
            <a
              href="/accessibility"
              className="text-[10px] font-bold text-violet-700 hover:text-violet-900 flex items-center gap-1"
              data-testid="a11y-more-link"
            >
              <Eye className="w-3 h-3" /> Learn more
            </a>
          </div>
        </div>
      )}

      <button
        type="button"
        aria-label={open ? 'Close accessibility panel' : 'Open accessibility panel'}
        onClick={() => setOpen(o => !o)}
        className={`flex items-center justify-center w-12 h-12 rounded-full shadow-lg transition-transform hover:scale-105 ${highContrast ? 'bg-yellow-300 text-black border-2 border-yellow-300' : 'bg-violet-700 text-white border-2 border-violet-700 hover:bg-violet-800'}`}
        data-testid="accessibility-toggle"
      >
        <Accessibility className="w-5 h-5" aria-hidden="true" />
      </button>
    </div>
  );
}

function SizeBtn({ label, pressed, onClick, testid }) {
  return (
    <button
      type="button"
      aria-pressed={pressed}
      onClick={onClick}
      className={`flex-1 rounded-lg border-2 py-1.5 text-xs font-bold transition ${pressed ? 'bg-violet-700 text-white border-violet-700' : 'bg-white text-slate-700 border-slate-300 hover:border-violet-400'}`}
      data-testid={testid}
    >
      {label}
    </button>
  );
}
